import { useAuth, useSignIn, useSignUp } from "@clerk/clerk-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { clerkCode, clerkFailure } from "../lib/clerkError";
import { canSupplySignUp, signUpDiagnostic } from "../lib/signUpStatus";

type LoadedSignUp = Extract<ReturnType<typeof useSignUp>, { isLoaded: true }>["signUp"];
type Mode = "password" | "sign-up" | "sign-up-code" | "reset-email" | "reset-password" | "code";
type CodeStage = "first" | "second";

type Failure = { summary: string; detail: string };

function normalizeCode(value: string): string {
  return value.trim().replace(/\s+/g, "");
}

const FIELD_LABELS: Record<string, string> = {
  email_address: "邮箱",
  password: "密码",
  legal_accepted: "服务条款",
  captcha: "人机验证",
  username: "用户名",
  first_name: "名字",
  last_name: "姓氏",
};

function fieldList(fields: readonly string[]): string {
  return fields.map((field) => FIELD_LABELS[field] ?? field).join("、");
}

export function SignInScreen() {
  const { isSignedIn } = useAuth();
  const signInApi = useSignIn();
  const signUpApi = useSignUp();
  const signInRef = useRef(signInApi);
  const signUpRef = useRef(signUpApi);
  signInRef.current = signInApi;
  signUpRef.current = signUpApi;
  const isLoaded = signInApi.isLoaded && signUpApi.isLoaded;
  const [mode, setMode] = useState<Mode>("password");
  const [codeStage, setCodeStage] = useState<CodeStage>("second");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const signedInRef = useRef(isSignedIn);
  signedInRef.current = isSignedIn;

  useEffect(() => {
    if (isSignedIn) window.location.assign("/");
  }, [isSignedIn]);

  function report(cause: unknown, fallback: string, summary?: string) {
    if (clerkCode(cause).includes("session_exists")) {
      window.location.assign("/");
      return;
    }
    const next = clerkFailure(cause, fallback);
    setFailure(summary ? { ...next, summary } : next);
  }

  async function enter(sessionId: string, diagnostic = "") {
    const activate = signUpRef.current.setActive ?? signInRef.current.setActive;
    if (!activate) {
      setFailure({
        summary: "登录会话没有激活。",
        detail: ["setActive 不可用", diagnostic].filter(Boolean).join("\n"),
      });
      return;
    }
    try {
      await activate({ session: sessionId });
    } catch (cause) {
      if (clerkCode(cause).includes("session_exists")) {
        window.location.assign("/");
        return;
      }
      const next = clerkFailure(cause, "登录会话没有激活。");
      setFailure({
        summary: next.summary,
        detail: [next.detail, diagnostic].filter(Boolean).join("\n"),
      });
      return;
    }
    window.location.assign("/");
  }

  async function continueSignIn(attempt: {
    status: string | null;
    createdSessionId: string | null;
    supportedFirstFactors: { strategy: string; emailAddressId?: string }[] | null;
    supportedSecondFactors: { strategy: string; emailAddressId?: string }[] | null;
  }) {
    const { signIn } = signInRef.current;
    if (!signIn) return;
    if (attempt.status === "complete" && attempt.createdSessionId) {
      await enter(attempt.createdSessionId);
      return;
    }
    if (attempt.status === "needs_new_password") {
      setMode("reset-password");
      setFailure(null);
      return;
    }
    if (attempt.status === "needs_second_factor") {
      const factor = attempt.supportedSecondFactors?.find((item) => item.strategy === "email_code");
      if (factor?.emailAddressId) {
        await signIn.prepareSecondFactor({ strategy: "email_code", emailAddressId: factor.emailAddressId });
        setCodeStage("second");
        setMode("code");
        setFailure(null);
        return;
      }
    }
    if (attempt.status === "needs_first_factor") {
      const factor = attempt.supportedFirstFactors?.find((item) => item.strategy === "email_code");
      if (factor?.emailAddressId) {
        await signIn.prepareFirstFactor({ strategy: "email_code", emailAddressId: factor.emailAddressId });
        setCodeStage("first");
        setMode("code");
        setFailure(null);
        return;
      }
    }
    setFailure({ summary: "还需要邮箱验证码才能继续。请查收邮件。", detail: attempt.status ?? "" });
  }

  async function signInWithPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signIn } = signInRef.current;
    if (!isLoaded || !signIn) return;
    setBusy(true);
    setFailure(null);
    try {
      const attempt = await signIn.create({
        strategy: "password",
        identifier: email.trim(),
        password,
      });
      await continueSignIn(attempt);
    } catch (cause) {
      report(cause, "登录没有完成。");
    } finally {
      setBusy(false);
    }
  }

  async function sendResetCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signIn } = signInRef.current;
    if (!isLoaded || !signIn) return;
    setBusy(true);
    setFailure(null);
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email.trim(),
      });
      setMode("reset-password");
    } catch (cause) {
      const missing = clerkCode(cause).includes("form_identifier_not_found");
      report(cause, "验证码没有发出去。", missing ? "账号不存在，请先注册" : undefined);
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signIn } = signInRef.current;
    if (!isLoaded || !signIn) return;
    if (password.length < 15) {
      setFailure({ summary: "密码至少 15 位。", detail: "" });
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      let attempt = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: normalizeCode(code),
        password,
      });
      if (attempt.status === "needs_new_password") {
        attempt = await signIn.resetPassword({ password, signOutOfOtherSessions: false });
      }
      await continueSignIn(attempt);
    } catch (cause) {
      report(cause, "密码没有设上。");
    } finally {
      setBusy(false);
    }
  }

  function requirementsMessage(attempt: { missingFields: readonly string[]; unverifiedFields: readonly string[] }): string {
    const missing = fieldList(attempt.missingFields);
    const unverified = fieldList(attempt.unverifiedFields);
    const parts = ["还没完成注册。"];
    if (missing) parts.push(`还缺：${missing}。`);
    if (unverified) parts.push(`未验证：${unverified}。`);
    return parts.join("");
  }

  function sendToLogin(diagnostic: string) {
    setMode("password");
    setCode("");
    setFailure({ summary: "账号已创建，请用邮箱和密码登录", detail: diagnostic });
  }

  async function concludeSignUp(attempt: LoadedSignUp, supplied = false, priorDetail = "") {
    const diagnostic = [priorDetail, signUpDiagnostic(attempt)].filter(Boolean).join("\n");
    if (isSignedIn || signedInRef.current) {
      window.location.assign("/");
      return;
    }
    if (attempt.status === "complete" && attempt.createdSessionId) {
      await enter(attempt.createdSessionId, diagnostic);
      return;
    }
    if (attempt.status === "complete") {
      sendToLogin(diagnostic);
      return;
    }
    if (attempt.status === "missing_requirements") {
      if (!supplied && canSupplySignUp(attempt)) {
        if (password.length < 15) {
          setFailure({
            summary: attempt.missingFields.includes("password") ? "密码至少 15 位。" : requirementsMessage(attempt),
            detail: diagnostic,
          });
          return;
        }
        try {
          const updated = await attempt.update({ password });
          await concludeSignUp(updated, true, priorDetail);
          return;
        } catch (cause) {
          if (clerkCode(cause).includes("session_exists") || signedInRef.current) {
            window.location.assign("/");
            return;
          }
          if (clerkCode(cause).includes("identifier_exists")) {
            sendToLogin([clerkFailure(cause, "").detail, diagnostic].filter(Boolean).join("\n"));
            return;
          }
          const next = clerkFailure(cause, "注册还没完成，请再试一次。");
          setFailure({ summary: next.summary, detail: [next.detail, diagnostic].filter(Boolean).join("\n") });
          return;
        }
      }
      setFailure({ summary: requirementsMessage(attempt), detail: diagnostic });
      return;
    }
    setFailure({
      summary: "注册还没完成，请再试一次。",
      detail: [attempt.status ?? "", diagnostic].filter(Boolean).join("\n"),
    });
  }

  async function recoverVerifiedSignUp(priorDetail = "") {
    const { signUp } = signUpRef.current;
    if (signedInRef.current) {
      window.location.assign("/");
      return;
    }
    if (!signUp) {
      sendToLogin("");
      return;
    }
    let current: LoadedSignUp = signUp;
    try {
      current = await signUp.reload();
    } catch {
      // The failed attempt left the previous sign-up in memory.
    }
    await concludeSignUp(current, false, priorDetail);
  }

  async function register(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signUp } = signUpRef.current;
    if (!isLoaded || !signUp) return;
    if (password.length < 15) {
      setFailure({ summary: "密码至少 15 位。", detail: "" });
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const created = await signUp.create({ emailAddress: email.trim(), password });
      await created.prepareEmailAddressVerification({ strategy: "email_code" });
      setCode("");
      setMode("sign-up-code");
    } catch (cause) {
      report(cause, "注册没有完成。");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function resendSignUpCode() {
    const { signUp } = signUpRef.current;
    if (!isLoaded || !signUp || busyRef.current) return;
    if (!signUp.id) {
      setFailure({ summary: "注册状态丢了，请返回重新注册。这一步不会新建账号。", detail: "" });
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setFailure(null);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
    } catch (cause) {
      if (clerkCode(cause).includes("already_verified")) {
        await recoverVerifiedSignUp(clerkFailure(cause, "没能重新发送验证码。").detail);
        return;
      }
      const next = clerkFailure(cause, "没能重新发送验证码。");
      setFailure({ summary: next.summary, detail: [next.detail, signUpDiagnostic(signUp)].filter(Boolean).join("\n") });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function verifySignUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signUp } = signUpRef.current;
    if (!isLoaded || !signUp) return;
    if (isSignedIn || signedInRef.current) {
      window.location.assign("/");
      return;
    }
    if (busyRef.current) return;
    if (!signUp.id) {
      setFailure({
        summary: "注册状态丢了，请返回重新注册。不要刷新页面。",
        detail: signUpDiagnostic(signUp),
      });
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setFailure(null);
    try {
      if (signUp.verifications.emailAddress.status === "verified") {
        await recoverVerifiedSignUp();
        return;
      }
      const attempt = await signUp.attemptEmailAddressVerification({ code: normalizeCode(code) });
      await concludeSignUp(attempt);
    } catch (cause) {
      if (clerkCode(cause).includes("session_exists") || signedInRef.current) {
        window.location.assign("/");
        return;
      }
      if (clerkCode(cause).includes("already_verified")) {
        await recoverVerifiedSignUp(clerkFailure(cause, "验证码没有通过。").detail);
        return;
      }
      if (clerkCode(cause).includes("identifier_exists")) {
        sendToLogin([clerkFailure(cause, "").detail, signUpDiagnostic(signUp)].filter(Boolean).join("\n"));
        return;
      }
      const next = clerkFailure(cause, "验证码没有通过。");
      setFailure({
        summary: next.summary,
        detail: [next.detail, signUpDiagnostic(signUpRef.current.signUp ?? signUp)].filter(Boolean).join("\n"),
      });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function verifyCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { signIn } = signInRef.current;
    if (!isLoaded || !signIn) return;
    setBusy(true);
    setFailure(null);
    try {
      const attempt =
        codeStage === "second"
          ? await signIn.attemptSecondFactor({ strategy: "email_code", code: normalizeCode(code) })
          : await signIn.attemptFirstFactor({ strategy: "email_code", code: normalizeCode(code) });
      await continueSignIn(attempt);
    } catch (cause) {
      report(cause, "验证码没有通过。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      {mode === "password" ? (
        <form className="gate-card" method="post" action="/" autoComplete="on" onSubmit={signInWithPassword}>
          <span className="logo-mark">准</span>
          <h1>登录 MediaDesk</h1>
          <p>用邮箱和密码登录。第一次使用请先注册。</p>
          <label>
            邮箱
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            密码
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <AuthError failure={failure} />
          <button type="submit" className="button primary" disabled={!isLoaded || busy}>
            {busy ? "请稍等" : "登录"}
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => {
              setMode("reset-email");
              setPassword("");
              setFailure(null);
            }}
          >
            忘记密码
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => {
              setMode("sign-up");
              setPassword("");
              setCode("");
              setFailure(null);
            }}
          >
            首次使用？注册
          </button>
        </form>
      ) : null}

      {mode === "sign-up" || mode === "sign-up-code" ? (
        <form
          className="gate-card"
          method="post"
          action="/"
          autoComplete="on"
          onSubmit={mode === "sign-up" ? register : verifySignUp}
        >
          <div id="clerk-captcha" />
          <span className="logo-mark">准</span>
          <h1>{mode === "sign-up" ? "注册 MediaDesk" : "验证邮箱"}</h1>
          <p>
            {mode === "sign-up"
              ? "填邮箱和新密码。验证码通过后会进入工作台。不在允许名单里的邮箱，服务端仍会拒绝。"
              : `验证码已发到 ${email || "你的邮箱"}。通过后会登录并进入工作台。`}
          </p>
          {mode === "sign-up" ? (
            <>
              <label>
                邮箱
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label>
                密码
                <input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  minLength={15}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <small>至少 15 位</small>
              </label>
            </>
          ) : (
            <label>
              验证码
              <input
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </label>
          )}
          <AuthError failure={failure} />
          <button type="submit" className="button primary" disabled={!isLoaded || busy}>
            {busy ? "请稍等" : mode === "sign-up" ? "注册" : "完成注册"}
          </button>
          {mode === "sign-up-code" ? (
            <button type="button" className="text-action" onClick={() => void resendSignUpCode()}>
              重新发送验证码
            </button>
          ) : (
            <button type="button" className="text-action" onClick={() => setMode("password")}>
              返回登录
            </button>
          )}
        </form>
      ) : null}

      {mode === "reset-email" ? (
        <form className="gate-card" onSubmit={sendResetCode}>
          <span className="logo-mark">准</span>
          <h1>忘记密码</h1>
          <p>验证码会发到已注册的邮箱。还没有账号请先注册。</p>
          <label>
            邮箱
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <AuthError failure={failure} />
          <button type="submit" className="button primary" disabled={!isLoaded || busy}>
            {busy ? "请稍等" : "发送验证码"}
          </button>
          <button type="button" className="text-action" onClick={() => setMode("password")}>
            返回登录
          </button>
        </form>
      ) : null}

      {mode === "reset-password" ? (
        <form className="gate-card" method="post" action="/" autoComplete="on" onSubmit={resetPassword}>
          <span className="logo-mark">准</span>
          <h1>设置新密码</h1>
          <p>把邮件里的验证码和新密码填在一起。设好后会直接登录。</p>
          <label>
            验证码
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
            />
          </label>
          <label>
            新密码
            <input
              type="password"
              name="new-password"
              autoComplete="new-password"
              minLength={15}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <small>至少 15 位</small>
          </label>
          <AuthError failure={failure} />
          <button type="submit" className="button primary" disabled={!isLoaded || busy}>
            {busy ? "请稍等" : "设置密码并登录"}
          </button>
          <button type="button" className="text-action" onClick={() => setMode("reset-email")}>
            重新发送验证码
          </button>
        </form>
      ) : null}

      {mode === "code" ? (
        <form className="gate-card" onSubmit={verifyCode}>
          <span className="logo-mark">准</span>
          <h1>再确认一次</h1>
          <p>Clerk 要求用邮箱验证码完成这一步。验证码已发到 {email || "你的邮箱"}。</p>
          <label>
            验证码
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
            />
          </label>
          <AuthError failure={failure} />
          <button type="submit" className="button primary" disabled={!isLoaded || busy}>
            {busy ? "请稍等" : "继续登录"}
          </button>
        </form>
      ) : null}
    </div>
  );
}

function AuthError({ failure }: { failure: Failure | null }) {
  if (!failure) return null;
  return (
    <div>
      <p className="inline-error">{failure.summary}</p>
      {failure.detail ? <p className="muted gate-error-detail">{failure.detail}</p> : null}
    </div>
  );
}
